import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Wallet as WalletIcon, Plus, Send, Download, CreditCard, Smartphone, ArrowDownLeft, ArrowUpRight, Loader2, Bitcoin, Building2, Trash2, Star, ArrowRightLeft } from 'lucide-react';

export default function Wallet() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [balance, setBalance] = useState(0);
  const [transfers, setTransfers] = useState({ sent: [], received: [] });
  const [paymentRequests, setPaymentRequests] = useState({ incoming: [], outgoing: [] });
  const [loading, setLoading] = useState(true);
  const [addFundsAmount, setAddFundsAmount] = useState('');
  const [sendAmount, setSendAmount] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [requestAmount, setRequestAmount] = useState('');
  const [requestRecipient, setRequestRecipient] = useState('');
  const [mobileProvider, setMobileProvider] = useState('mpesa');
  const [mobilePhone, setMobilePhone] = useState('');
  const [mobileAmount, setMobileAmount] = useState('');
  
  // External wallets state
  const [externalWallets, setExternalWallets] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [showAddWalletDialog, setShowAddWalletDialog] = useState(false);
  const [showWithdrawDialog, setShowWithdrawDialog] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [walletType, setWalletType] = useState('crypto');
  const [walletForm, setWalletForm] = useState({
    name: '',
    crypto_currency: 'btc',
    wallet_address: '',
    network: '',
    bank_name: '',
    account_number: '',
    routing_number: '',
    swift_code: '',
    account_holder_name: '',
    country: ''
  });
  const [withdrawAmount, setWithdrawAmount] = useState('');

  useEffect(() => {
    loadWalletData();
    
    const paymentStatus = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    if (paymentStatus === 'success' && sessionId) {
      checkTopupStatus(sessionId);
    }
  }, []);

  const loadWalletData = async () => {
    try {
      const [balanceRes, transfersRes, incomingReqRes, outgoingReqRes, walletsRes, withdrawalsRes] = await Promise.all([
        api.get('/wallet/balance'),
        api.get('/wallet/transfers'),
        api.get('/payment-requests/incoming'),
        api.get('/payment-requests/outgoing'),
        api.get('/wallets/external'),
        api.get('/withdrawals')
      ]);
      
      setBalance(balanceRes.data.balance);
      setTransfers(transfersRes.data);
      setPaymentRequests({
        incoming: incomingReqRes.data,
        outgoing: outgoingReqRes.data
      });
      setExternalWallets(walletsRes.data || []);
      setWithdrawals(withdrawalsRes.data || []);
    } catch (error) {
      toast.error('Failed to load wallet data');
    } finally {
      setLoading(false);
    }
  };

  const checkTopupStatus = async (sessionId, attempt = 0) => {
    try {
      const response = await api.get(`/wallet/topup-status/${sessionId}`);
      if (response.data.payment_status === 'paid') {
        toast.success('Funds added successfully!');
        loadWalletData();
      } else if (attempt < 5) {
        setTimeout(() => checkTopupStatus(sessionId, attempt + 1), 2000);
      }
    } catch (error) {
      console.error('Failed to check topup status');
    }
  };

  const handleAddFunds = async () => {
    if (!addFundsAmount || parseFloat(addFundsAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    try {
      const response = await api.post('/wallet/add-funds', {
        amount: parseFloat(addFundsAmount),
        origin_url: window.location.origin
      });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Failed to initiate payment');
    }
  };

  const handleSendToUser = async () => {
    if (!recipientEmail || !sendAmount || parseFloat(sendAmount) <= 0) {
      toast.error('Please fill all fields');
      return;
    }

    try {
      await api.post('/wallet/send-to-user', {
        recipient_email: recipientEmail,
        amount: parseFloat(sendAmount),
        note: 'Wallet transfer'
      });
      toast.success('Money sent successfully!');
      setSendAmount('');
      setRecipientEmail('');
      loadWalletData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send money');
    }
  };

  const handleRequestPayment = async () => {
    if (!requestRecipient || !requestAmount || parseFloat(requestAmount) <= 0) {
      toast.error('Please fill all fields');
      return;
    }

    try {
      await api.post('/payment-requests/create', {
        recipient_email: requestRecipient,
        amount: parseFloat(requestAmount),
        currency: 'USD',
        note: 'Payment request'
      });
      toast.success('Payment request sent!');
      setRequestAmount('');
      setRequestRecipient('');
      loadWalletData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create payment request');
    }
  };

  const handlePayRequest = async (requestId) => {
    try {
      await api.post(`/payment-requests/${requestId}/pay`);
      toast.success('Payment completed!');
      loadWalletData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment failed');
    }
  };

  const handleInitiateMobileMoney = async () => {
    if (!mobilePhone || !mobileAmount || parseFloat(mobileAmount) <= 0) {
      toast.error('Please fill all fields');
      return;
    }

    try {
      const response = await api.post('/mobile-money/initiate', {
        provider: mobileProvider,
        phone_number: mobilePhone,
        amount: parseFloat(mobileAmount),
        currency: 'USD'
      });
      toast.success(response.data.instructions);
      setMobileAmount('');
      setMobilePhone('');
    } catch (error) {
      toast.error('Failed to initiate mobile money payment');
    }
  };

  // External Wallet Functions
  const handleAddWallet = async () => {
    try {
      const payload = {
        wallet_type: walletType,
        name: walletForm.name,
        ...(walletType === 'crypto' ? {
          crypto_currency: walletForm.crypto_currency,
          wallet_address: walletForm.wallet_address,
          network: walletForm.network
        } : {
          bank_name: walletForm.bank_name,
          account_number: walletForm.account_number,
          routing_number: walletForm.routing_number,
          swift_code: walletForm.swift_code,
          account_holder_name: walletForm.account_holder_name,
          country: walletForm.country
        })
      };
      
      await api.post('/wallets/external', payload);
      toast.success('Wallet added successfully');
      setShowAddWalletDialog(false);
      setWalletForm({
        name: '',
        crypto_currency: 'btc',
        wallet_address: '',
        network: '',
        bank_name: '',
        account_number: '',
        routing_number: '',
        swift_code: '',
        account_holder_name: '',
        country: ''
      });
      loadWalletData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add wallet');
    }
  };

  const handleDeleteWallet = async (walletId) => {
    if (!confirm('Are you sure you want to delete this wallet?')) return;
    
    try {
      await api.delete(`/wallets/external/${walletId}`);
      toast.success('Wallet deleted');
      loadWalletData();
    } catch (error) {
      toast.error('Failed to delete wallet');
    }
  };

  const handleSetDefault = async (walletId) => {
    try {
      await api.put(`/wallets/external/${walletId}/default`);
      toast.success('Default wallet updated');
      loadWalletData();
    } catch (error) {
      toast.error('Failed to set default wallet');
    }
  };

  const handleWithdraw = async () => {
    if (!selectedWallet || !withdrawAmount) {
      toast.error('Please select a wallet and enter an amount');
      return;
    }
    
    const amount = parseFloat(withdrawAmount);
    if (amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    if (amount > balance) {
      toast.error('Insufficient balance');
      return;
    }
    
    try {
      const endpoint = selectedWallet.wallet_type === 'crypto' ? '/withdraw/crypto' : '/withdraw/bank';
      const response = await api.post(endpoint, {
        wallet_id: selectedWallet.id,
        amount: amount,
        currency: 'USD'
      });
      
      toast.success(response.data.message);
      setShowWithdrawDialog(false);
      setWithdrawAmount('');
      setSelectedWallet(null);
      loadWalletData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process withdrawal');
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'pending': 'secondary',
      'pending_approval': 'secondary',
      'pending_manual': 'secondary',
      'processing': 'default',
      'approved': 'default',
      'completed': 'default',
      'failed': 'destructive',
      'rejected': 'destructive'
    };
    return statusMap[status] || 'outline';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
          <Loader2 className="w-8 h-8 animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">Wallet</h1>
          <p className="text-muted-foreground">Manage your funds and transactions</p>
        </div>

        <Card data-testid="wallet-balance-card" className="p-8 mb-8 bg-gradient-to-br from-primary to-blue-900 text-white">
          <div className="flex items-center gap-3 mb-4">
            <WalletIcon className="w-6 h-6" />
            <span className="text-sm uppercase tracking-[0.2em] opacity-90">Available Balance</span>
          </div>
          <p className="text-5xl font-bold font-mono tabular-nums mb-6">${balance.toFixed(2)}</p>
          <div className="flex gap-3">
            <Dialog>
              <DialogTrigger asChild>
                <Button data-testid="add-funds-btn" className="bg-white text-primary hover:bg-white/90 gap-2">
                  <Plus className="w-4 h-4" /> Add Funds (Card)
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Funds to Wallet</DialogTitle>
                  <DialogDescription>Top up your wallet using credit/debit card</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Amount (USD)</Label>
                    <Input
                      data-testid="add-funds-amount-input"
                      type="number"
                      step="0.01"
                      placeholder="100.00"
                      value={addFundsAmount}
                      onChange={(e) => setAddFundsAmount(e.target.value)}
                    />
                  </div>
                  <Button data-testid="confirm-add-funds-btn" onClick={handleAddFunds} className="w-full bg-accent hover:bg-accent/90">
                    Continue to Payment
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            <Button
              data-testid="add-crypto-btn"
              onClick={() => navigate('/crypto')}
              className="bg-white/20 text-white hover:bg-white/30 gap-2"
            >
              <Bitcoin className="w-4 h-4" /> Add Funds (Crypto)
            </Button>
          </div>
        </Card>

        <Tabs defaultValue="transfer" className="space-y-6">
          <TabsList>
            <TabsTrigger value="transfer" data-testid="tab-transfer">Send & Receive</TabsTrigger>
            <TabsTrigger value="external" data-testid="tab-external" className="gap-2">
              <ArrowRightLeft className="w-4 h-4" /> External Wallets
            </TabsTrigger>
            <TabsTrigger value="requests" data-testid="tab-requests">Payment Requests</TabsTrigger>
            <TabsTrigger value="mobile" data-testid="tab-mobile">Mobile Money</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
          </TabsList>

          <TabsContent value="transfer">
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="p-6">
                <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <Send className="w-5 h-5 text-accent" />
                  Send Money
                </h3>
                <div className="space-y-4">
                  <div>
                    <Label>Recipient Email</Label>
                    <Input
                      data-testid="send-email-input"
                      type="email"
                      placeholder="recipient@example.com"
                      value={recipientEmail}
                      onChange={(e) => setRecipientEmail(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Amount (USD)</Label>
                    <Input
                      data-testid="send-amount-input"
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={sendAmount}
                      onChange={(e) => setSendAmount(e.target.value)}
                    />
                  </div>
                  <Button data-testid="send-money-btn" onClick={handleSendToUser} className="w-full bg-accent hover:bg-accent/90">
                    Send Money
                  </Button>
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <Download className="w-5 h-5 text-accent" />
                  Request Payment
                </h3>
                <div className="space-y-4">
                  <div>
                    <Label>From Email</Label>
                    <Input
                      data-testid="request-email-input"
                      type="email"
                      placeholder="payer@example.com"
                      value={requestRecipient}
                      onChange={(e) => setRequestRecipient(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Amount (USD)</Label>
                    <Input
                      data-testid="request-amount-input"
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={requestAmount}
                      onChange={(e) => setRequestAmount(e.target.value)}
                    />
                  </div>
                  <Button data-testid="request-payment-btn" onClick={handleRequestPayment} className="w-full bg-accent hover:bg-accent/90">
                    Request Payment
                  </Button>
                </div>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="external">
            <div className="space-y-6">
              {/* Add Wallet & Withdraw Buttons */}
              <div className="flex gap-4">
                <Dialog open={showAddWalletDialog} onOpenChange={setShowAddWalletDialog}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-wallet-btn" className="gap-2">
                      <Plus className="w-4 h-4" /> Add Wallet
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>Add External Wallet</DialogTitle>
                      <DialogDescription>Add a crypto wallet or bank account for withdrawals</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div>
                        <Label>Wallet Type</Label>
                        <Select value={walletType} onValueChange={setWalletType}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="crypto">Crypto Wallet</SelectItem>
                            <SelectItem value="bank">Bank Account</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <Label>Wallet Name</Label>
                        <Input 
                          placeholder={walletType === 'crypto' ? 'My BTC Wallet' : 'My Bank Account'}
                          value={walletForm.name}
                          onChange={(e) => setWalletForm({...walletForm, name: e.target.value})}
                        />
                      </div>
                      
                      {walletType === 'crypto' ? (
                        <>
                          <div>
                            <Label>Cryptocurrency</Label>
                            <Select 
                              value={walletForm.crypto_currency}
                              onValueChange={(v) => setWalletForm({...walletForm, crypto_currency: v})}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="btc">Bitcoin (BTC)</SelectItem>
                                <SelectItem value="eth">Ethereum (ETH)</SelectItem>
                                <SelectItem value="usdt">USDT</SelectItem>
                                <SelectItem value="usdc">USDC</SelectItem>
                                <SelectItem value="ltc">Litecoin (LTC)</SelectItem>
                                <SelectItem value="trx">TRON (TRX)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Wallet Address</Label>
                            <Input 
                              placeholder="Enter wallet address"
                              value={walletForm.wallet_address}
                              onChange={(e) => setWalletForm({...walletForm, wallet_address: e.target.value})}
                            />
                          </div>
                          <div>
                            <Label>Network (Optional)</Label>
                            <Select 
                              value={walletForm.network}
                              onValueChange={(v) => setWalletForm({...walletForm, network: v})}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select network" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="auto">Auto-detect</SelectItem>
                                <SelectItem value="erc20">ERC-20 (Ethereum)</SelectItem>
                                <SelectItem value="trc20">TRC-20 (TRON)</SelectItem>
                                <SelectItem value="bep20">BEP-20 (BSC)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </>
                      ) : (
                        <>
                          <div>
                            <Label>Bank Name</Label>
                            <Input 
                              placeholder="Chase Bank"
                              value={walletForm.bank_name}
                              onChange={(e) => setWalletForm({...walletForm, bank_name: e.target.value})}
                            />
                          </div>
                          <div>
                            <Label>Account Holder Name</Label>
                            <Input 
                              placeholder="John Doe"
                              value={walletForm.account_holder_name}
                              onChange={(e) => setWalletForm({...walletForm, account_holder_name: e.target.value})}
                            />
                          </div>
                          <div>
                            <Label>Account Number</Label>
                            <Input 
                              placeholder="Enter account number"
                              value={walletForm.account_number}
                              onChange={(e) => setWalletForm({...walletForm, account_number: e.target.value})}
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label>Routing Number</Label>
                              <Input 
                                placeholder="021000021"
                                value={walletForm.routing_number}
                                onChange={(e) => setWalletForm({...walletForm, routing_number: e.target.value})}
                              />
                            </div>
                            <div>
                              <Label>SWIFT Code</Label>
                              <Input 
                                placeholder="CHASUS33"
                                value={walletForm.swift_code}
                                onChange={(e) => setWalletForm({...walletForm, swift_code: e.target.value})}
                              />
                            </div>
                          </div>
                          <div>
                            <Label>Country</Label>
                            <Input 
                              placeholder="US"
                              value={walletForm.country}
                              onChange={(e) => setWalletForm({...walletForm, country: e.target.value})}
                            />
                          </div>
                        </>
                      )}
                      
                      <Button onClick={handleAddWallet} className="w-full" data-testid="save-wallet-btn">
                        Add Wallet
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
                
                <Dialog open={showWithdrawDialog} onOpenChange={setShowWithdrawDialog}>
                  <DialogTrigger asChild>
                    <Button variant="outline" data-testid="withdraw-btn" className="gap-2">
                      <ArrowUpRight className="w-4 h-4" /> Withdraw
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Withdraw Funds</DialogTitle>
                      <DialogDescription>Withdraw to your external wallet or bank account</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div>
                        <Label>Select Wallet</Label>
                        <Select 
                          value={selectedWallet?.id || ''}
                          onValueChange={(v) => setSelectedWallet(externalWallets.find(w => w.id === v))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Choose a wallet" />
                          </SelectTrigger>
                          <SelectContent>
                            {externalWallets.map((wallet) => (
                              <SelectItem key={wallet.id} value={wallet.id}>
                                {wallet.wallet_type === 'crypto' ? (
                                  <span className="flex items-center gap-2">
                                    <Bitcoin className="w-4 h-4" />
                                    {wallet.name} ({wallet.crypto_currency?.toUpperCase()})
                                  </span>
                                ) : (
                                  <span className="flex items-center gap-2">
                                    <Building2 className="w-4 h-4" />
                                    {wallet.name} ({wallet.bank_name})
                                  </span>
                                )}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {selectedWallet && (
                        <div className="p-3 bg-muted rounded-lg text-sm">
                          {selectedWallet.wallet_type === 'crypto' ? (
                            <>
                              <p><strong>Address:</strong> {selectedWallet.wallet_address?.substring(0, 20)}...</p>
                              <p><strong>Currency:</strong> {selectedWallet.crypto_currency?.toUpperCase()}</p>
                              <p className="text-xs text-muted-foreground mt-1">Min. withdrawal: $10</p>
                            </>
                          ) : (
                            <>
                              <p><strong>Bank:</strong> {selectedWallet.bank_name}</p>
                              <p><strong>Account:</strong> ****{selectedWallet.account_number?.slice(-4)}</p>
                              <p className="text-xs text-muted-foreground mt-1">Min. withdrawal: $50 | Processing: 1-3 days</p>
                            </>
                          )}
                        </div>
                      )}
                      
                      <div>
                        <Label>Amount (USD)</Label>
                        <div className="flex gap-2">
                          <Input 
                            type="number"
                            placeholder="0.00"
                            value={withdrawAmount}
                            onChange={(e) => setWithdrawAmount(e.target.value)}
                          />
                          <Button variant="outline" onClick={() => setWithdrawAmount(balance.toString())}>
                            Max
                          </Button>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">Available: ${balance.toFixed(2)}</p>
                      </div>
                      
                      <Button onClick={handleWithdraw} className="w-full" data-testid="confirm-withdraw-btn">
                        Withdraw
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              {/* Wallets Grid */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Crypto Wallets */}
                <Card className="p-6">
                  <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                    <Bitcoin className="w-5 h-5 text-orange-500" />
                    Crypto Wallets
                  </h3>
                  {externalWallets.filter(w => w.wallet_type === 'crypto').length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">No crypto wallets added</p>
                  ) : (
                    <div className="space-y-3">
                      {externalWallets.filter(w => w.wallet_type === 'crypto').map((wallet) => (
                        <div key={wallet.id} data-testid={`crypto-wallet-${wallet.id}`} className="flex justify-between items-center p-3 border rounded-lg">
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium">{wallet.name}</p>
                              {wallet.is_default && <Badge variant="default" className="text-xs">Default</Badge>}
                            </div>
                            <p className="text-sm text-muted-foreground">{wallet.crypto_currency?.toUpperCase()}</p>
                            <p className="text-xs text-muted-foreground font-mono">{wallet.wallet_address?.substring(0, 20)}...</p>
                          </div>
                          <div className="flex gap-1">
                            {!wallet.is_default && (
                              <Button variant="ghost" size="sm" onClick={() => handleSetDefault(wallet.id)}>
                                <Star className="w-4 h-4" />
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteWallet(wallet.id)}>
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>

                {/* Bank Accounts */}
                <Card className="p-6">
                  <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-blue-500" />
                    Bank Accounts
                  </h3>
                  {externalWallets.filter(w => w.wallet_type === 'bank').length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">No bank accounts added</p>
                  ) : (
                    <div className="space-y-3">
                      {externalWallets.filter(w => w.wallet_type === 'bank').map((wallet) => (
                        <div key={wallet.id} data-testid={`bank-wallet-${wallet.id}`} className="flex justify-between items-center p-3 border rounded-lg">
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium">{wallet.name}</p>
                              {wallet.is_default && <Badge variant="default" className="text-xs">Default</Badge>}
                            </div>
                            <p className="text-sm text-muted-foreground">{wallet.bank_name}</p>
                            <p className="text-xs text-muted-foreground">****{wallet.account_number?.slice(-4)}</p>
                          </div>
                          <div className="flex gap-1">
                            {!wallet.is_default && (
                              <Button variant="ghost" size="sm" onClick={() => handleSetDefault(wallet.id)}>
                                <Star className="w-4 h-4" />
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteWallet(wallet.id)}>
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </div>

              {/* Withdrawal History */}
              <Card className="p-6">
                <h3 className="text-xl font-semibold mb-4">Withdrawal History</h3>
                {withdrawals.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No withdrawals yet</p>
                ) : (
                  <div className="space-y-3">
                    {withdrawals.map((withdrawal) => (
                      <div key={withdrawal.id} data-testid={`withdrawal-${withdrawal.id}`} className="flex justify-between items-center p-4 border rounded-lg">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${withdrawal.wallet_type === 'crypto' ? 'bg-orange-500/10' : 'bg-blue-500/10'}`}>
                            {withdrawal.wallet_type === 'crypto' ? (
                              <Bitcoin className="w-5 h-5 text-orange-500" />
                            ) : (
                              <Building2 className="w-5 h-5 text-blue-500" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium">
                              {withdrawal.wallet_type === 'crypto' 
                                ? `${withdrawal.crypto_currency?.toUpperCase()} Withdrawal` 
                                : `Bank Transfer`}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {withdrawal.wallet_type === 'crypto' 
                                ? `${withdrawal.wallet_address?.substring(0, 15)}...`
                                : withdrawal.bank_name}
                            </p>
                            <p className="text-xs text-muted-foreground">{formatDate(withdrawal.created_at)}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-mono font-bold">${withdrawal.amount?.toFixed(2)}</p>
                          <Badge variant={getStatusBadge(withdrawal.status)}>{withdrawal.status?.replace('_', ' ')}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="requests">
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="p-6">
                <h3 className="text-xl font-semibold mb-4">Incoming Requests</h3>
                {paymentRequests.incoming.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No incoming requests</p>
                ) : (
                  <div className="space-y-4">
                    {paymentRequests.incoming.map((request) => (
                      <div key={request.id} className="p-4 border rounded-lg">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-medium">{request.from_user_name}</p>
                            <p className="text-sm text-muted-foreground">{request.from_user_email}</p>
                          </div>
                          <p className="font-mono font-bold text-lg">${request.amount.toFixed(2)}</p>
                        </div>
                        <p className="text-sm text-muted-foreground mb-3">{request.note}</p>
                        <Button
                          data-testid={`pay-request-${request.id}`}
                          onClick={() => handlePayRequest(request.id)}
                          size="sm"
                          className="w-full bg-accent hover:bg-accent/90"
                        >
                          Pay Now
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              <Card className="p-6">
                <h3 className="text-xl font-semibold mb-4">Outgoing Requests</h3>
                {paymentRequests.outgoing.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No outgoing requests</p>
                ) : (
                  <div className="space-y-4">
                    {paymentRequests.outgoing.map((request) => (
                      <div key={request.id} className="p-4 border rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium">To: User</p>
                            <p className="text-sm text-muted-foreground">{request.note}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-mono font-bold">${request.amount.toFixed(2)}</p>
                            <Badge variant={request.status === 'paid' ? 'default' : 'secondary'} className="capitalize mt-1">
                              {request.status}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="mobile">
            <Card className="p-6 max-w-2xl mx-auto">
              <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-green-500" />
                M-Pesa (Safaricom Ethiopia)
              </h3>
              <p className="text-muted-foreground mb-6">Send and receive money via M-Pesa mobile money</p>

              <Tabs defaultValue="send-mpesa" className="space-y-4">
                <TabsList>
                  <TabsTrigger value="send-mpesa" data-testid="mpesa-tab-send">Send Money</TabsTrigger>
                  <TabsTrigger value="receive-mpesa" data-testid="mpesa-tab-receive">Receive Money</TabsTrigger>
                  <TabsTrigger value="mpesa-history" data-testid="mpesa-tab-history">History</TabsTrigger>
                </TabsList>

                <TabsContent value="send-mpesa">
                  <div className="space-y-4">
                    <div>
                      <Label>Recipient Phone Number</Label>
                      <Input
                        data-testid="mpesa-send-phone"
                        placeholder="+251 9XX XXX XXX"
                        value={mobilePhone}
                        onChange={(e) => setMobilePhone(e.target.value)}
                      />
                      <p className="text-xs text-muted-foreground mt-1">Ethiopian M-Pesa number (e.g., 251911234567)</p>
                    </div>
                    <div>
                      <Label>Amount (ETB)</Label>
                      <Input
                        data-testid="mpesa-send-amount"
                        type="number"
                        step="1"
                        placeholder="100"
                        value={mobileAmount}
                        onChange={(e) => setMobileAmount(e.target.value)}
                      />
                    </div>
                    <Button
                      data-testid="mpesa-send-btn"
                      onClick={async () => {
                        if (!mobilePhone || !mobileAmount || parseFloat(mobileAmount) <= 0) {
                          toast.error('Please fill all fields');
                          return;
                        }
                        try {
                          const response = await api.post('/mpesa/send', {
                            phone_number: mobilePhone,
                            amount: parseFloat(mobileAmount),
                            remarks: 'EzyTransfer M-Pesa send',
                          });
                          if (response.data.status === 'success') {
                            toast.success(`Sent ETB ${mobileAmount} to ${mobilePhone}`);
                          } else {
                            toast.error(response.data.response?.responseMessage || response.data.response?.error || 'Payment failed');
                          }
                          setMobileAmount('');
                          setMobilePhone('');
                        } catch (error) {
                          toast.error(error.response?.data?.detail || 'M-Pesa send failed');
                        }
                      }}
                      className="w-full bg-green-600 hover:bg-green-700 text-white"
                    >
                      <Send className="w-4 h-4 mr-2" /> Send via M-Pesa
                    </Button>
                  </div>
                </TabsContent>

                <TabsContent value="receive-mpesa">
                  <div className="space-y-4">
                    <div>
                      <Label>Your Phone Number</Label>
                      <Input
                        data-testid="mpesa-receive-phone"
                        placeholder="+251 9XX XXX XXX"
                        value={mobilePhone}
                        onChange={(e) => setMobilePhone(e.target.value)}
                      />
                      <p className="text-xs text-muted-foreground mt-1">Your Ethiopian M-Pesa number to receive from</p>
                    </div>
                    <div>
                      <Label>Amount (ETB)</Label>
                      <Input
                        data-testid="mpesa-receive-amount"
                        type="number"
                        step="1"
                        placeholder="100"
                        value={mobileAmount}
                        onChange={(e) => setMobileAmount(e.target.value)}
                      />
                    </div>
                    <Button
                      data-testid="mpesa-receive-btn"
                      onClick={async () => {
                        if (!mobilePhone || !mobileAmount || parseFloat(mobileAmount) <= 0) {
                          toast.error('Please fill all fields');
                          return;
                        }
                        try {
                          const response = await api.post('/mpesa/receive', {
                            phone_number: mobilePhone,
                            amount: parseFloat(mobileAmount),
                            remarks: 'EzyTransfer M-Pesa receive',
                          });
                          if (response.data.status === 'success') {
                            toast.success(`Receive request sent for ETB ${mobileAmount}`);
                          } else {
                            toast.error(response.data.response?.responseMessage || response.data.response?.error || 'Receive failed');
                          }
                          setMobileAmount('');
                          setMobilePhone('');
                        } catch (error) {
                          toast.error(error.response?.data?.detail || 'M-Pesa receive failed');
                        }
                      }}
                      className="w-full bg-green-600 hover:bg-green-700 text-white"
                    >
                      <ArrowDownLeft className="w-4 h-4 mr-2" /> Request Payment via M-Pesa
                    </Button>
                  </div>
                </TabsContent>

                <TabsContent value="mpesa-history">
                  <MpesaHistory />
                </TabsContent>
              </Tabs>
            </Card>
          </TabsContent>

          <TabsContent value="history">
            <div className="space-y-6">
              <Card className="p-6">
                <h3 className="text-xl font-semibold mb-4">Sent Transfers</h3>
                {transfers.sent.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No sent transfers</p>
                ) : (
                  <div className="space-y-3">
                    {transfers.sent.map((transfer) => (
                      <div key={transfer.id} className="flex justify-between items-center p-4 border rounded-lg">
                        <div className="flex items-center gap-3">
                          <ArrowUpRight className="w-5 h-5 text-destructive" />
                          <div>
                            <p className="font-medium">{transfer.to_user_name}</p>
                            <p className="text-sm text-muted-foreground">{formatDate(transfer.created_at)}</p>
                          </div>
                        </div>
                        <p className="font-mono font-bold text-destructive">-${transfer.amount.toFixed(2)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              <Card className="p-6">
                <h3 className="text-xl font-semibold mb-4">Received Transfers</h3>
                {transfers.received.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No received transfers</p>
                ) : (
                  <div className="space-y-3">
                    {transfers.received.map((transfer) => (
                      <div key={transfer.id} className="flex justify-between items-center p-4 border rounded-lg">
                        <div className="flex items-center gap-3">
                          <ArrowDownLeft className="w-5 h-5 text-accent" />
                          <div>
                            <p className="font-medium">{transfer.from_user_name}</p>
                            <p className="text-sm text-muted-foreground">{formatDate(transfer.created_at)}</p>
                          </div>
                        </div>
                        <p className="font-mono font-bold text-accent">+${transfer.amount.toFixed(2)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}


function MpesaHistory() {
  const [txns, setTxns] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/mpesa/customer/history');
        setTxns(res.data || []);
      } catch {
        // No transactions yet
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) return <div className="text-center py-4"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;

  if (txns.length === 0) return <p className="text-muted-foreground text-center py-6">No M-Pesa transactions yet</p>;

  return (
    <div className="space-y-3">
      {txns.map((tx) => (
        <div key={tx.id} data-testid={`mpesa-tx-${tx.id}`} className="flex justify-between items-center p-4 border rounded-lg">
          <div className="flex items-center gap-3">
            {tx.direction === 'send' ? (
              <ArrowUpRight className="w-5 h-5 text-blue-500" />
            ) : (
              <ArrowDownLeft className="w-5 h-5 text-green-500" />
            )}
            <div>
              <p className="font-medium">{tx.direction === 'send' ? 'Sent' : 'Received'} via M-Pesa</p>
              <p className="text-sm text-muted-foreground font-mono">{tx.phone_number}</p>
              <p className="text-xs text-muted-foreground">{tx.created_at ? new Date(tx.created_at).toLocaleString() : ''}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="font-mono font-bold">{tx.direction === 'send' ? '-' : '+'}ETB {tx.amount}</p>
            <Badge variant={tx.success ? 'default' : 'destructive'} className="text-xs">
              {tx.success ? 'Success' : 'Failed'}
            </Badge>
          </div>
        </div>
      ))}
    </div>
  );
}
